import {useCallback, useContext, useMemo, useState} from 'react';

import * as Sentry from '@sentry/react';
import i18next from 'i18next';

import {LoadingContext} from '../contexts/LoadingContext/LoadingContext';
import {ApiCustomError} from '../lib/api/ApiCustomError';
import {Delete} from '../lib/api/DELETE/Delete';
import {Patch} from '../lib/api/PATCH/Patch';
import {Post} from '../lib/api/POST/Post';
import {Put} from '../lib/api/PUT/Put';
import {RequestBody} from '../lib/api/RequestBody/RequestBody';
import {ApiResponse, ApiResponseError, isApiResponseError} from '../lib/api/v1/types';
import {ApiSerializer} from '../serializers/ApiSerializer';
import {forceLogout, getUserAuthToken} from '../services/auth-service';

type WRITE_METHOD = 'post' | 'patch' | 'delete' | 'put';

const isLocalEnv = process.env.REACT_APP_ENV === 'local';

interface ApiWriteArgs<Response> {
  path?: string;
  method?: WRITE_METHOD;
  onSuccess?: (data: Response) => void;
  onError?: (error?: ApiResponseError) => void;
  disableLoadingOverlay?: boolean;

  /**
   *
   * Authenticates with this token instead of the session cookie. Needed by flows that hold a token
   * before the session exists, such as the two-factor step between sign-in and OTP verification.
   */
  authToken?: string;

  /**
   *
   * Overrides snake_case serializer when necessary
   */
  snakeCaseOverride?(data: any): any;
  /**
   *
   * Overrides camelCase serializer when necessary
   */
  camelCaseOverride?(data: any): Response;
}

export const useApiWrite = <Path extends Post | Put | Patch | Delete, Response extends object | void = void>(
  args?: ApiWriteArgs<Response>
) => {
  const method = args?.method || '';
  const path = args?.path || '';
  const onSuccess = args?.onSuccess;
  const onError = args?.onError;
  const disableLoadingOverlay = !!args?.disableLoadingOverlay;
  const authToken = args?.authToken;
  const snakeCaseOverride = args?.snakeCaseOverride;
  const camelCaseOverride = args?.camelCaseOverride;

  const {setLoadingContext} = useContext(LoadingContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [response, setResponse] = useState<ApiResponse<Response>>();

  const serialize = useMemo(
    () => ({
      toCamelCase: camelCaseOverride || ApiSerializer.toCamelCase<Response>,
      toSnakeCase: snakeCaseOverride || ApiSerializer.toSnakeCase<RequestBody<Path>[0]>,
    }),
    [camelCaseOverride, snakeCaseOverride]
  );

  const request = useCallback(
    async (body: any, argPath?: string, argMethod?: WRITE_METHOD) => {
      const token = authToken || getUserAuthToken();
      const methodToUse = argMethod || method;
      const pathToUse = argPath || path;
      const isFormDataBody = body instanceof FormData;

      const headers: HeadersInit = {
        'X-Application-Id': process.env.REACT_APP_PROJECT_ID ?? '',
        Authorization: `Bearer ${token}`,
        'Accept-Language': i18next.language,
      };
      if (!isFormDataBody) {
        headers['Content-Type'] = 'application/json';
      }

      if (isLocalEnv && process.env.REACT_APP_API_KEY) {
        headers['X-Application-Secret'] = process.env.REACT_APP_API_KEY;
      }

      return fetch(`${process.env.REACT_APP_API_HOST}${pathToUse}`, {
        method: methodToUse.toUpperCase(),
        headers,
        body: body ? (isFormDataBody ? body : JSON.stringify(body)) : undefined,
      });
    },
    [path, method, authToken]
  );

  const errorCallback = useCallback(
    (error?: any) => {
      const err = isApiResponseError(error)
        ? error
        : new ApiCustomError({error_code: 'CLI_0000', error_message: 'Web client error', extra_details: error.message});

      if (error.error_code === 'AUTH-UA-1') {
        return forceLogout();
      }
      Sentry.captureException(err);
      if (onError) {
        onError(err);
      }
      setResponse({
        ok: false,
        data: undefined,
        error: err,
      });
    },
    [onError, setResponse]
  );

  const successCallback = useCallback(
    (data?: any) => {
      const serializedData = serialize.toCamelCase(data);
      if (onSuccess) {
        onSuccess(serializedData);
      }
      setResponse({
        ok: true,
        data: serializedData,
        error: undefined,
      });
    },
    [onSuccess, setResponse, serialize]
  );

  const makeRequest = useCallback(
    async (...body: RequestBody<Path>) => {
      try {
        setIsLoading(true);
        if (!disableLoadingOverlay) {
          setLoadingContext(true);
        }
        const requestBody = body[0]
          ? body[0] instanceof FormData
            ? body[0]
            : serialize.toSnakeCase(body[0] as any)
          : {};
        const resp = await request(requestBody);
        let data = null;
        const contentType = resp.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          data = await resp.json();
        }
        if (!resp.ok) {
          if (isApiResponseError(data)) {
            throw new ApiCustomError(data);
          } else {
            const serializedErrorData = data && typeof data === 'object' ? ApiSerializer.toCamelCase<any>(data) : {};
            throw new ApiCustomError({
              error_code: 'CLI_API_0001',
              error_message: 'Request failed',
              extra_details: 'Could not connect to API, or unexpected response.',
              ...(serializedErrorData || {}),
            });
          }
        }
        return successCallback(data);
      } catch (error: any | ApiResponseError) {
        errorCallback(error);
      } finally {
        if (!disableLoadingOverlay) {
          setLoadingContext(false);
        }

        setIsLoading(false);
      }
    },
    [errorCallback, successCallback, setIsLoading, request, serialize, setLoadingContext, disableLoadingOverlay]
  );

  const makeManualRequestWithParams = async ({
    method: manualMethod,
    path: manualPath,
    body: manualBody,
  }: {
    method: WRITE_METHOD;
    path: string;
    body?: any;
  }) => {
    try {
      setIsLoading(true);
      if (!disableLoadingOverlay) {
        setLoadingContext(true);
      }
      const requestBody = manualBody
        ? manualBody instanceof FormData
          ? manualBody
          : serialize.toSnakeCase(manualBody)
        : {};
      const resp = await request(requestBody, manualPath, manualMethod);
      let data = null;
      const contentType = resp.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        data = await resp.json();
      }

      if (!resp.ok) {
        if (isApiResponseError(data)) {
          throw new ApiCustomError(data);
        } else {
          const serializedErrorData = data && typeof data === 'object' ? ApiSerializer.toCamelCase<any>(data) : {};
          throw new ApiCustomError({
            error_code: 'CLI_API_0001',
            error_message: 'Request failed',
            extra_details: 'Could not connect to API, or unexpected response.',
            ...(serializedErrorData || {}),
          });
        }
      }
      return successCallback(data);
    } catch (error: any | ApiResponseError) {
      errorCallback(error);
    } finally {
      if (!disableLoadingOverlay) {
        setLoadingContext(false);
      }

      setIsLoading(false);
    }
  };

  return {
    makeRequest,
    makeManualRequestWithParams, // Allows Manual requests that don't rely on initial input of hook.
    isLoading,
    ...response,
  };
};
